import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    FREE_CANVAS_ICON_BASE_URL,
    WHEEL_ZOOM_STEP,
} from '../constants/canvas';
import { FUNCTION_LABELS, FUNCTION_NODE_GROUPS } from '../components/AddNodePopover';
import {
    isEditableEvent,
    shouldBlockToolSwitchShortcut,
} from '../utils/common';
import { getNodeSize } from '../utils/graph';

/**
 * 画布交互管理（工具切换、右键菜单、拖拽、快捷键、浮层）
 */
export const useCanvasInteraction = ({
    isPmView,
    activeTool,
    setActiveTool,
    isSpacePressed,
    setIsSpacePressed,
    canvasEmpty,
    zoom,
    setZoom,
    nodes,
    nodesRef,
    edgesRef,
    reactFlowRef,
    canvasRef,
    activeGroupId,
    setActiveGroupId,
    setNodes,
    setSidebarCollapsed,
    setPanelDismissToken,
    selectedNodeIds,
    contextMenu,
    setContextMenu,
    connectionAddMenu,
    setConnectionAddMenu,
    closeContextMenu,
    closeConnectionAddMenu,
    clipboardRef,
    pasteCountRef,
    toolbarLoadingKey,
    runGroupSkipSelect,
    setRunGroupSkipSelect,
    uploadInputRef,
    setHistoryModalOpen,
    setMaterialLibraryOpen,
    setTemplateLibraryOpen,
    handleZoomChange,
    handleCopy,
    handlePaste,
    handleUndo,
    handleRedo,
    handleGroupSelection,
    handleUngroupSelection,
    handleDeleteSelection,
    handleAddNode,
    handleQuickAddNode,
    handleCopyNode,
    handleDuplicateNode,
    handleDeleteNode,
    handleCopyTaskId,
    handleRunGroup,
    handleBatchDownloadGroup,
    handleBatchDownloadSelection,
    handleDuplicateSelection,
    handleOpenSaveTemplateModal,
    isNodeDeletable,
    isNodeLocked,
    areNodesLocked,
    isGroupLocked,
    getSelectedGroupNodes,
    getSelectedGroupableNodes,
    getSelectedAppendableGroup,
    getSnapshot,
    buildClipboardPayloadFromState,
    cloneClipboardToCanvas,
    getDragDuplicateSourceNodeIds,
    pushHistory,
    persistCanvasBatch,
    scheduleViewportPersist,
    navigate,
    returnToStatePath,
    returnToStateTab,
    sourcePage,
    sourceTab,
    returnToPath,
    dragSnapshotRef,
}) => {
    const altDragDuplicateRef = useRef(null);

    // 处理画布工具栏的快捷动作
    const handleAction = useCallback((key) => {
        if (isPmView) return;
        if (key === 'upload') {
            uploadInputRef.current?.click();
            return;
        }

        if (key === 'record') {
            setHistoryModalOpen(true);
            return;
        }

        if (key === 'folder') {
            setMaterialLibraryOpen(true);
            return;
        }

        if (key === 'template') {
            setTemplateLibraryOpen(true);
        }
    }, [isPmView, uploadInputRef, setHistoryModalOpen, setMaterialLibraryOpen, setTemplateLibraryOpen]);

    // 切换当前画布工具
    const handleToolChange = useCallback((toolKey) => {
        if (isPmView) return;
        closeConnectionAddMenu();
        setActiveTool(toolKey);
    }, [closeConnectionAddMenu, isPmView, setActiveTool]);

    // 打开右键菜单并记录触发位置
    const openContextMenu = useCallback((event, scope, nodeId = null) => {
        if (isPmView) return;
        event.preventDefault();
        event.stopPropagation();
        closeConnectionAddMenu();

        const reactFlow = reactFlowRef.current;
        const canvas = canvasRef.current;
        const flowPosition = reactFlow && canvas
            ? reactFlow.screenToFlowPosition({ x: event.clientX, y: event.clientY })
            : null;

        setContextMenu({
            open: true,
            scope,
            x: event.clientX,
            y: event.clientY,
            nodeId,
            flowPosition,
        });
    }, [closeConnectionAddMenu, isPmView, reactFlowRef, canvasRef, setContextMenu]);

    // 打开节点级右键菜单
    const handleNodeContextMenu = useCallback((event, node) => {
        openContextMenu(event, 'node', node.id);
    }, [openContextMenu]);

    // 点击节点时同步激活所属分组
    const handleNodeClick = useCallback((event, node) => {
        event.preventDefault();
        event.stopPropagation();
        closeContextMenu();
        closeConnectionAddMenu();

        if (node.type === 'groupNode') {
            setPanelDismissToken((prev) => prev + 1);
        }

        const groupTargetId = node.type === 'groupNode'
            ? node.id
            : node.parentId || null;

        if (!groupTargetId) {
            setActiveGroupId(null);
            return;
        }

        setActiveGroupId(groupTargetId);

        setNodes((prev) => prev.map((item) => {
            if (item.id === groupTargetId) {
                return {
                    ...item,
                    selected: true,
                };
            }

            if (item.parentId === groupTargetId) {
                return {
                    ...item,
                    selected: false,
                };
            }

            return {
                ...item,
                selected: false,
            };
        }));
    }, [closeConnectionAddMenu, closeContextMenu, setActiveGroupId, setNodes, setPanelDismissToken]);

    // 打开画布空白区域右键菜单
    const handlePaneContextMenu = useCallback((event) => {
        openContextMenu(event, 'pane');
    }, [openContextMenu]);

    // 点击画布空白区域时收起附加状态
    const handlePaneClick = useCallback(() => {
        setSidebarCollapsed(true);
        closeContextMenu();
        setActiveGroupId(null);
        setPanelDismissToken((prev) => prev + 1);
    }, [closeContextMenu, setActiveGroupId, setPanelDismissToken, setSidebarCollapsed]);

    // 拖拽节点开始时记录快照
    const handleNodeDragStart = useCallback((event, node) => {
        const snapshot = getSnapshot();
        dragSnapshotRef.current = snapshot;

        const shouldDuplicateOnDrag = !!event?.altKey && !isPmView && !!node?.id;
        if (!shouldDuplicateOnDrag) {
            altDragDuplicateRef.current = null;
            return;
        }

        const sourceNodeIds = getDragDuplicateSourceNodeIds(node.id);
        if (sourceNodeIds.length === 0 || areNodesLocked(sourceNodeIds)) {
            altDragDuplicateRef.current = null;
            return;
        }

        altDragDuplicateRef.current = {
            anchorNodeId: String(node.id),
            sourceNodeIds,
            snapshot,
        };

        const previewClipboard = buildClipboardPayloadFromState(
            sourceNodeIds,
            snapshot.nodes,
            snapshot.edges,
        );
        cloneClipboardToCanvas(previewClipboard, {
            baseNodes: snapshot.nodes,
            baseEdges: snapshot.edges,
            positionDelta: { x: 0, y: 0 },
            skipHistory: true,
            skipPersist: true,
            skipNextImageSequenceUpdate: true,
            selectDuplicatedNodes: false,
        });
    }, [areNodesLocked, buildClipboardPayloadFromState, cloneClipboardToCanvas, dragSnapshotRef, getDragDuplicateSourceNodeIds, getSnapshot, isPmView]);

    // 拖拽节点结束后写入历史并保存位置
    const handleNodeDragStop = useCallback((event, node) => {
        if (!dragSnapshotRef.current) return;

        const before = dragSnapshotRef.current.nodes;
        const beforeEdges = dragSnapshotRef.current.edges;
        const after = nodesRef.current;
        const changed = before.some((node, index) => (
            node.position.x !== after[index]?.position?.x
            || node.position.y !== after[index]?.position?.y
        ));

        const altDragState = altDragDuplicateRef.current;
        if (altDragState?.anchorNodeId) {
            const beforeAnchorNode = before.find((item) => item.id === altDragState.anchorNodeId);
            const afterAnchorNode = after.find((item) => item.id === altDragState.anchorNodeId);
            const deltaX = (afterAnchorNode?.position?.x ?? 0) - (beforeAnchorNode?.position?.x ?? 0);
            const deltaY = (afterAnchorNode?.position?.y ?? 0) - (beforeAnchorNode?.position?.y ?? 0);

            altDragDuplicateRef.current = null;
            dragSnapshotRef.current = null;

            if (deltaX === 0 && deltaY === 0) {
                return;
            }

            const clipboard = buildClipboardPayloadFromState(
                altDragState.sourceNodeIds,
                before,
                beforeEdges,
            );
            cloneClipboardToCanvas(clipboard, {
                baseNodes: before,
                baseEdges: beforeEdges,
                historySnapshot: altDragState.snapshot,
                positionDelta: {
                    x: deltaX,
                    y: deltaY,
                },
            });
            return;
        }

        if (changed) {
            pushHistory(dragSnapshotRef.current);
            persistCanvasBatch({
                beforeNodes: before,
                beforeEdges,
                afterNodes: after,
                afterEdges: edgesRef.current,
            });
        }
        dragSnapshotRef.current = null;
        altDragDuplicateRef.current = null;
    }, [buildClipboardPayloadFromState, cloneClipboardToCanvas, dragSnapshotRef, persistCanvasBatch, pushHistory, nodesRef, edgesRef]);

    // 根据当前上下文生成右键菜单项
    const contextMenuItems = useMemo(() => {
        if (isPmView) return [];
        const pasteDisabled = !clipboardRef.current?.nodes?.length;
        const contextNode = contextMenu.nodeId
            ? nodesRef.current.find((node) => node.id === contextMenu.nodeId)
            : null;
        const contextNodeDeleteDisabled = contextNode ? !isNodeDeletable(contextNode.id) : false;
        const contextNodeModifyDisabled = contextNode ? isNodeLocked(contextNode.id) : false;

        if (contextMenu.scope === 'node' && contextMenu.nodeId) {
            return [
                { key: 'copy-node', label: '复制节点', shortcut: 'Ctrl C', disabled: false, onClick: () => handleCopyNode(contextMenu.nodeId) },
                { key: 'duplicate-node', label: '创建副本', disabled: contextNodeModifyDisabled, onClick: () => handleDuplicateNode(contextMenu.nodeId) },
                {
                    key: 'paste-node',
                    label: '粘贴节点',
                    shortcut: 'Ctrl V',
                    disabled: pasteDisabled,
                    onClick: () => handlePaste(contextMenu.flowPosition),
                },
                { key: 'divider-delete', type: 'divider' },
                { key: 'delete-node', label: '删除节点', shortcut: 'Delete', disabled: contextNodeDeleteDisabled, onClick: () => handleDeleteNode(contextMenu.nodeId) },
                { key: 'copy-task-id', label: '复制调试信息', onClick: () => handleCopyTaskId(contextMenu.nodeId) },
            ];
        }

        return [
            {
                key: 'add-node-title',
                type: 'title',
                label: '添加节点',
            },
            {
                key: 'add-image-node',
                label: '图片',
                icon: `${FREE_CANVAS_ICON_BASE_URL}/node-image.svg`,
                onClick: () => handleAddNode('image', {
                    flowPosition: contextMenu.flowPosition,
                    autoOpenFunctionPanel: true,
                }),
            },
            { key: 'divider-add-node', type: 'divider' },
            ...FUNCTION_NODE_GROUPS
                .filter((item) => FUNCTION_LABELS[item.key])
                .map((item) => ({
                    key: `function-${item.key}`,
                    label: item.label,
                    icon: item.icon,
                    onClick: () => handleAddNode(item.key, {
                        flowPosition: contextMenu.flowPosition,
                        autoOpenFunctionPanel: true,
                    }),
                })),
            { key: 'divider-paste', type: 'divider' },
            {
                key: 'paste',
                label: '粘贴',
                disabled: pasteDisabled,
                onClick: () => handlePaste(contextMenu.flowPosition),
            },
        ];
    }, [contextMenu.flowPosition, contextMenu.nodeId, contextMenu.scope, handleAddNode, handleCopyNode, handleCopyTaskId, handleDeleteNode, handleDuplicateNode, handlePaste, isNodeDeletable, isNodeLocked, isPmView, clipboardRef, nodesRef]);

    const connectionAddMenuItems = useMemo(() => {
        if (!connectionAddMenu.open || !connectionAddMenu.sourceNodeId) return [];

        return [
            {
                key: 'connection-add-node-title',
                type: 'title',
                label: '增加节点',
            },
            ...FUNCTION_NODE_GROUPS
                .filter((item) => FUNCTION_LABELS[item.key])
                .map((item) => ({
                    key: `connection-function-${item.key}`,
                    label: item.label,
                    icon: item.icon,
                    onClick: () => handleQuickAddNode(
                        connectionAddMenu.sourceNodeId,
                        item.key,
                        { flowPosition: connectionAddMenu.flowPosition },
                    ),
                })),
        ];
    }, [connectionAddMenu, handleQuickAddNode]);

    // 计算分组操作浮层的展示状态和位置
    const groupActionToolbar = useMemo(() => {
        if (isPmView) {
            return {
                visible: false,
                nodeIds: [],
                position: null,
                items: [],
            };
        }
        const selectedGroupNodes = getSelectedGroupNodes();
        const selectedGroupableNodes = getSelectedGroupableNodes();
        const selectedTopLevelNodes = nodes.filter((node) => node.selected && !node.parentId);
        const appendableGroupSelection = getSelectedAppendableGroup();
        const activeGroupNode = activeGroupId
            ? nodes.find((node) => node.id === activeGroupId && node.type === 'groupNode')
            : null;

        if (appendableGroupSelection) {
            return {
                visible: false,
                nodeIds: [],
                position: null,
                items: [],
            };
        }

        if (activeGroupNode) {
            const groupNode = activeGroupNode;
            const groupLocked = isGroupLocked(groupNode.id);

            return {
                visible: true,
                nodeIds: [groupNode.id],
                position: null,
                items: [
                    {
                        key: 'run-group',
                        label: '整组执行',
                        icon: 'start',
                        popover: {
                            title: '跳过选图',
                            hint: '开启后，将直接执行全部任务，每个算法节点默认生成一张图片\n关闭后，按单节点内设置的是否选图执行',
                            checked: runGroupSkipSelect,
                            onChange: setRunGroupSkipSelect,
                        },
                        disabled: groupLocked,
                        onClick: handleRunGroup,
                    },
                    {
                        key: 'ungroup-group',
                        label: '解组',
                        icon: 'focus',
                        disabled: groupLocked,
                        onClick: () => handleUngroupSelection([groupNode.id]),
                    },
                    {
                        key: 'duplicate-group',
                        label: '创建副本',
                        icon: 'copy',
                        disabled: groupLocked,
                        onClick: () => handleDuplicateNode(groupNode.id),
                    },
                    {
                        key: 'save-group-template',
                        label: '存为模板',
                        icon: 'template',
                        disabled: groupLocked,
                        onClick: () => handleOpenSaveTemplateModal(groupNode.id),
                    },
                    {
                        key: 'download-group',
                        label: '批量下载',
                        icon: 'download',
                        loading: toolbarLoadingKey === 'download-group',
                        disabled: groupLocked,
                        onClick: handleBatchDownloadGroup,
                    },
                ],
            };
        }

        if (selectedGroupNodes.length === 0 && selectedGroupableNodes.length >= 2 && selectedGroupableNodes.length === selectedTopLevelNodes.length) {
            const createGroupDisabled = areNodesLocked(selectedGroupableNodes.map((node) => node.id));
            return {
                visible: true,
                nodeIds: selectedGroupableNodes.map((node) => node.id),
                position: (() => {
                    const reactFlow = reactFlowRef.current;
                    const canvas = canvasRef.current;
                    if (!reactFlow || !canvas) return null;

                    const bounds = selectedGroupableNodes.reduce((acc, node) => {
                        const { width, height } = getNodeSize(node);

                        return {
                            minX: Math.min(acc.minX, node.position.x),
                            minY: Math.min(acc.minY, node.position.y),
                            maxX: Math.max(acc.maxX, node.position.x + width),
                            maxY: Math.max(acc.maxY, node.position.y + height),
                        };
                    }, {
                        minX: Number.POSITIVE_INFINITY,
                        minY: Number.POSITIVE_INFINITY,
                        maxX: Number.NEGATIVE_INFINITY,
                        maxY: Number.NEGATIVE_INFINITY,
                    });

                    const canvasBounds = canvas.getBoundingClientRect();
                    const screenPosition = reactFlow.flowToScreenPosition({
                        x: (bounds.minX + bounds.maxX) / 2,
                        y: bounds.minY,
                    });

                    return {
                        x: Math.max(canvasBounds.left + 60, screenPosition.x),
                        y: screenPosition.y - 12,
                    };
                })(),
                items: [
                    {
                        key: 'duplicate-selection',
                        label: '创建副本',
                        icon: 'copy',
                        disabled: createGroupDisabled,
                        onClick: () => handleDuplicateSelection(selectedGroupableNodes.map((node) => node.id)),
                    },
                    {
                        key: 'download-selection',
                        label: '批量下载',
                        icon: 'download',
                        loading: toolbarLoadingKey === 'download-selection',
                        disabled: createGroupDisabled,
                        onClick: () => handleBatchDownloadSelection(selectedGroupableNodes.map((node) => node.id)),
                    },
                ],
            };
        }

        return {
            visible: false,
            nodeIds: [],
            position: null,
            items: [],
        };
    }, [
        activeGroupId,
        getSelectedGroupNodes,
        getSelectedGroupableNodes,
        getSelectedAppendableGroup,
        handleBatchDownloadGroup,
        handleBatchDownloadSelection,
        areNodesLocked,
        handleDuplicateSelection,
        handleDuplicateNode,
        handleOpenSaveTemplateModal,
        handleRunGroup,
        handleUngroupSelection,
        isGroupLocked,
        nodes,
        runGroupSkipSelect,
        toolbarLoadingKey,
        isPmView,
        reactFlowRef,
        canvasRef,
        setRunGroupSkipSelect,
    ]);

    // 全局快捷键处理
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (isEditableEvent(event)) return;

            if (event.code === 'Space' && !event.ctrlKey && !event.metaKey) {
                event.preventDefault();
                setIsSpacePressed(true);
                return;
            }

            const hasModifier = event.ctrlKey || event.metaKey;
            const key = event.key.toLowerCase();

            if (isPmView) {
                if (!hasModifier) return;

                const isZoomIn = event.key === '+' || event.key === '=' || event.code === 'NumpadAdd';
                const isZoomOut = event.key === '-' || event.key === '_' || event.code === 'NumpadSubtract';

                if (!isZoomIn && !isZoomOut) return;

                event.preventDefault();
                event.stopPropagation();
                if (canvasEmpty) return;

                const currentZoom = Math.round((reactFlowRef.current?.getZoom?.() ?? zoom / 100) * 100);
                const nextZoom = currentZoom + (isZoomIn ? WHEEL_ZOOM_STEP : -WHEEL_ZOOM_STEP);
                handleZoomChange(nextZoom);
                return;
            }

            if (!hasModifier && key === 'v') {
                if (shouldBlockToolSwitchShortcut()) return;
                event.preventDefault();
                event.stopPropagation();
                setActiveTool('select');
                return;
            }

            if (!hasModifier && key === 'h') {
                if (shouldBlockToolSwitchShortcut()) return;
                event.preventDefault();
                event.stopPropagation();
                setActiveTool('drag');
                return;
            }

            if (hasModifier && key === 'c') {
                event.preventDefault();
                event.stopPropagation();
                handleCopy();
                return;
            }

            if (hasModifier && key === 'v') {
                event.preventDefault();
                event.stopPropagation();
                handlePaste();
                return;
            }

            if (hasModifier && key === 'z') {
                event.preventDefault();
                event.stopPropagation();
                if (event.shiftKey) {
                    handleRedo();
                } else {
                    handleUndo();
                }
                return;
            }

            if (hasModifier && key === 'y') {
                event.preventDefault();
                event.stopPropagation();
                handleRedo();
                return;
            }

            if (hasModifier && key === 'g') {
                event.preventDefault();
                event.stopPropagation();
                if (event.shiftKey) {
                    handleUngroupSelection();
                } else {
                    handleGroupSelection();
                }
                return;
            }

            if (event.key === 'Delete' || event.key === 'Backspace') {
                event.preventDefault();
                event.stopPropagation();
                handleDeleteSelection();
                return;
            }

            if (!hasModifier) return;

            const isZoomIn = event.key === '+' || event.key === '=' || event.code === 'NumpadAdd';
            const isZoomOut = event.key === '-' || event.key === '_' || event.code === 'NumpadSubtract';

            if (!isZoomIn && !isZoomOut) return;

            event.preventDefault();
            event.stopPropagation();
            if (canvasEmpty) return;

            const currentZoom = Math.round((reactFlowRef.current?.getZoom?.() ?? zoom / 100) * 100);
            const nextZoom = currentZoom + (isZoomIn ? WHEEL_ZOOM_STEP : -WHEEL_ZOOM_STEP);

            handleZoomChange(nextZoom);
        };

        const handleKeyUp = (event) => {
            if (event.code === 'Space') {
                setIsSpacePressed(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [canvasEmpty, handleCopy, handleDeleteSelection, handleGroupSelection, handlePaste, handleRedo, handleUndo, handleUngroupSelection, handleZoomChange, isPmView, zoom, reactFlowRef, setActiveTool, setIsSpacePressed]);

    const handleBack = useCallback(() => {
        if (isPmView || sourcePage === 'myProject') {
            navigate('/home/myProject/list', {
                replace: true,
                state: { tab: returnToStateTab || sourceTab || (isPmView ? '2' : '1') },
            });
            return;
        }

        if (returnToStatePath) {
            navigate(returnToStatePath, { replace: true });
            return;
        }

        if (returnToPath) {
            navigate(returnToPath, { replace: true });
            return;
        }

        if (typeof window !== 'undefined') {
            const referrer = document.referrer || '';
            const currentOrigin = window.location.origin || '';
            const hasInternalReferrer = !!referrer && referrer.startsWith(currentOrigin);

            if (hasInternalReferrer && window.history.length > 1) {
                navigate(-1);
                return;
            }
        }

        navigate('/home/homemain/creativeToImage', { replace: true });
    }, [isPmView, navigate, returnToPath, returnToStatePath, returnToStateTab, sourcePage, sourceTab]);

    return {
        handleAction,
        handleToolChange,
        openContextMenu,
        handleNodeContextMenu,
        handleNodeClick,
        handlePaneContextMenu,
        handlePaneClick,
        handleNodeDragStart,
        handleNodeDragStop,
        handleBack,
        contextMenuItems,
        connectionAddMenuItems,
        groupActionToolbar,
    };
};